import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import useAuth from '../../auth/useAuth';

import Layout from '../UI/introGameLayout/layout';
import PopupMessages from '../UI/introGameLayout/popupMessages';
import Analysis from '../UI/game/analysis';

import {
  generalInfoSummaryQuery,
  scoresItemInsightsQuery,
} from './api';

import { popupMessagesSummaryApc, summaryApcCategories } from './data';

import './styles.css';

const BAR_COLOR = '#B4E6EA';

// Average non-null/undefined numeric values. Returns null when the set is
// empty — callers decide how to render a missing bar.
function average(values) {
  const nums = values.filter(v => v != null && !Number.isNaN(v));
  if (!nums.length) return null;
  return nums.reduce((sum, v) => sum + v, 0) / nums.length;
}

export default function ThePrinciplesSummaryApc() {
  const navigate = useNavigate();

  const { getUser, getCurrentThePrinciplesGameId } = useAuth();
  const user = getUser();
  const gameId = getCurrentThePrinciplesGameId();

  const [currentGameSessionId, setCurrentGameSessionId] = useState(0);

  const { data: generalInfo } = useQuery(generalInfoSummaryQuery()) || {};
  const gamesSessions = generalInfo?.sessions;

  const { data: scoresItemInsights } = useQuery(scoresItemInsightsQuery({ gameId }));

  useEffect(() => {
    if (!gamesSessions || !gameId || !user?.id) return;

    const currentGameSessions = gamesSessions.filter(
      session => session.user === user.id && session.game === gameId
    );
    const inProgress = currentGameSessions.find(s => !s.completed);
    if (inProgress) {
      setCurrentGameSessionId(inProgress.id);
      return;
    }
    const mostRecentCompleted = currentGameSessions
      .filter(s => s.completed)
      .reduce((max, s) => (s.id > (max?.id || 0) ? s : max), null);
    if (mostRecentCompleted) setCurrentGameSessionId(mostRecentCompleted.id);
  }, [gamesSessions, gameId, user?.id]);

  // Pick the insight record for the active game session. Fall back to the
  // most recent record by attempt number if we can't match the session yet.
  const sessionInsight = useMemo(() => {
    if (!scoresItemInsights?.length) return null;
    const matched = scoresItemInsights.find(r => r.game_session === currentGameSessionId);
    if (matched) return matched;
    return [...scoresItemInsights].sort(
      (a, b) => (b.attempt || 0) - (a.attempt || 0)
    )[0] || null;
  }, [scoresItemInsights, currentGameSessionId]);

  // Map item_name -> accuracy (0..1) for O(1) lookup.
  const accuracyByName = useMemo(() => {
    const map = new Map();
    const items = sessionInsight?.accuracy?.items || [];
    items.forEach(item => {
      if (item?.item_name) map.set(item.item_name, item.accuracy);
    });
    return map;
  }, [sessionInsight]);

  // Per-category bars (Assumptions / Principles / Constraints). When the
  // scenario has no account items under a category (nothing in the insights
  // response for any of its items), flag the bar as not applicable instead
  // of rendering a 0% bar.
  const categoryBars = useMemo(
    () => summaryApcCategories.map(c => {
      const accuracies = c.items
        .map(name => accuracyByName.get(name))
        .filter(v => v != null);
      if (!accuracies.length) {
        return {
          title: c.key,
          score: 0,
          color: BAR_COLOR,
          notApplicable: true,
        };
      }
      const avg = average(accuracies);
      return {
        title: c.key,
        score: avg != null ? avg * 100 : 0,
        color: BAR_COLOR,
      };
    }),
    [accuracyByName]
  );

  // Per-item bars — drop any item that has no data or a null accuracy so
  // the chart only shows rows the user actually interacted with.
  const itemBars = useMemo(
    () => summaryApcCategories.flatMap(c =>
      c.items
        .map(name => ({ name, accuracy: accuracyByName.get(name) }))
        .filter(({ accuracy }) => accuracy != null)
        .map(({ name, accuracy }) => ({
          title: name,
          score: accuracy * 100,
          color: BAR_COLOR,
        }))
    ),
    [accuracyByName]
  );

  const data = useMemo(() => ([
    { bars: categoryBars, barsLabel: '%' },
    { title: 'Assumptions, Principles, and Constraints', bars: itemBars, barsLabel: '%' },
  ]), [categoryBars, itemBars]);

  const maxScore = useMemo(
    () => Math.max(100, ...data.flatMap(section => section.bars?.map(b => b.score) || [])),
    [data]
  );

  const dataLoaded = !!sessionInsight;

  const handleNextClick = () => {
    navigate('/the-principles/summary/breakdown');
  };

  const handleBackClick = () => {
    navigate('/the-principles/summary');
  };

  return (
    <Layout
      backgroundColor="#FBEDD5"
      isBackgroundBlurred={false}
      isHeaderVisible={true}
      isFooterVisible={false}
    >
      {dataLoaded && (
        <div className="game-summary-container apc-summary-container">
          <PopupMessages
            messages={popupMessagesSummaryApc}
            messageSize="small"
            className="apc-summary-message"
            buttonText="Next"
            onButtonClick={handleNextClick}
            additionalButtonText="Back"
            onAdditionalButtonClick={handleBackClick}
          />
          <Analysis
            heading="Accuracy Breakdown"
            data={data}
            size="small"
            maxScore={maxScore}
          />
        </div>
      )}
    </Layout>
  );
}
